import { FOLDER_UPLOAD_RATE_WINDOW_MS } from './reducers';
import type { FolderUploadJobState, FolderUploadRateSample } from './types';

/** The rate needs at least this much history before it predicts anything. */
const MIN_RATE_SPAN_MS = 2_000;

export type FolderUploadJobPhase =
  | 'running'
  | 'done'
  /** Finished with at least one failed file. */
  | 'failed'
  | 'cancelled'
  /** Refused as a whole before any file started. */
  | 'blocked';

export type FolderUploadJobSummary = {
  phase: FolderUploadJobPhase;
  /** Files still part of the upload (cancelled ones are left out). */
  filesTotal: number;
  filesDone: number;
  /** Done plus files whose bytes are all sent and are being filed. */
  filesSent: number;
  filesFailed: number;
  filesCancelled: number;
  filesActive: number;
  bytesTotal: number;
  bytesDone: number;
  /** 0–1, by bytes, or by files when every file is empty. */
  fraction: number;
  /** Seconds left at the recent rate; null until the rate is known. */
  etaSeconds: number | null;
  retryableFailedCount: number;
};

/** Bytes per second over the recent window, or null without enough data. */
export function folderUploadTransferRate(
  samples: ReadonlyArray<FolderUploadRateSample>,
  now: number
): number | null {
  const recent = samples.filter(
    (sample) => sample.at >= now - FOLDER_UPLOAD_RATE_WINDOW_MS
  );
  const first = recent[0];
  const last = recent.at(-1);
  if (!first || !last || last.at - first.at < MIN_RATE_SPAN_MS) return null;
  const bytes = last.bytes - first.bytes;
  if (bytes <= 0) return null;
  return bytes / ((last.at - first.at) / 1000);
}

export function summarizeFolderUploadJob(
  job: FolderUploadJobState,
  now: number
): FolderUploadJobSummary {
  let filesTotal = 0;
  let filesDone = 0;
  let filesRegistering = 0;
  let filesFailed = 0;
  let filesCancelled = 0;
  let filesActive = 0;
  let bytesTotal = 0;
  let bytesDone = 0;
  let retryableFailedCount = 0;
  for (const file of job.files) {
    if (file.status === 'cancelled') {
      filesCancelled += 1;
      continue;
    }
    filesTotal += 1;
    bytesTotal += file.sizeBytes;
    bytesDone += file.loadedBytes;
    if (file.status === 'done') filesDone += 1;
    else if (file.status === 'failed') {
      filesFailed += 1;
      if (file.retryable) retryableFailedCount += 1;
    } else {
      filesActive += 1;
      if (file.status === 'registering') filesRegistering += 1;
    }
  }
  const phase: FolderUploadJobPhase = job.blockedReason
    ? 'blocked'
    : job.running
      ? 'running'
      : // An upload the member stopped with Cancel all reads as cancelled,
        // never as a failure, even when some files had failed (#5823 TASTE).
        // (only when it actually cancelled something: files already being
        // filed cannot be, and then the job finished on its own).
        job.cancelRequested && filesCancelled > 0
        ? 'cancelled'
        : filesFailed > 0
          ? 'failed'
          : filesCancelled > 0 && filesDone === 0
            ? 'cancelled'
            : 'done';
  const fraction =
    bytesTotal > 0
      ? bytesDone / bytesTotal
      : filesTotal > 0
        ? filesDone / filesTotal
        : 1;
  const rate =
    phase === 'running' ? folderUploadTransferRate(job.rateSamples, now) : null;
  const remaining = Math.max(0, bytesTotal - bytesDone);
  return {
    phase,
    filesTotal,
    filesDone,
    filesSent: filesDone + filesRegistering,
    filesFailed,
    filesCancelled,
    filesActive,
    bytesTotal,
    bytesDone,
    fraction: Math.min(1, Math.max(0, fraction)),
    etaSeconds: rate ? Math.ceil(remaining / rate) : null,
    retryableFailedCount,
  };
}

/** True while any job still sends or files something. */
export function hasActiveFolderUploads(
  jobs: ReadonlyArray<FolderUploadJobState>
): boolean {
  return jobs.some((job) => job.running);
}
